import api from './apiService';

// Wire contracts for Splashtop RMM remote access (AgentAPI rmm/splashtop).
// Mirrors Defatom.Cloud.AgentAPI.Expose/RemoteAccess/RemoteAccessContracts.cs — change both together.
// No real-time channel: the device's agent reports its state on a schedule, and Connect returns the launch data directly.

export type RemoteAccessClientType = 'app' | 'web';

/** Splashtop RMM viewer installers — needed on the technician's machine for client type "app" (st-rmm:// links). */
export const SPLASHTOP_VIEWER_DOWNLOADS = [
  { label: 'Windows', url: 'https://my.splashtop.com/rmm/win' },
  { label: 'macOS', url: 'https://my.splashtop.com/rmm/mac' },
];

export const RemoteAccessErrorCodes = {
  NotEnabled: 'NOT_ENABLED',
  GroupNotEnabled: 'GROUP_NOT_ENABLED',
  SettingsUnavailable: 'SETTINGS_UNAVAILABLE',
  AgentInactive: 'AGENT_INACTIVE',
  AgentNotFound: 'AGENT_NOT_FOUND',
  NotReady: 'NOT_READY',
  StreamerOffline: 'STREAMER_OFFLINE',
  StreamerNotFound: 'STREAMER_NOT_FOUND',
  ServiceUnavailable: 'SERVICE_UNAVAILABLE',
  NotConfigured: 'NOT_CONFIGURED',
  Internal: 'INTERNAL',
} as const;

export interface StreamerStatus {
  installed: boolean;
  running: boolean;
  associated: boolean;
  streamerUuid?: string;
  streamerVersion?: string;
  installedByPatchifi: boolean;
  lastError?: string;
  reportedUtc: string;
}

export interface SplashtopFeatureStatus {
  enabled: boolean;
  code?: string;
  message: string;
  clientType: RemoteAccessClientType;
}

export interface RemoteAccessAgentStatus {
  agentId: string;
  enabled: boolean;
  serviceConfigured: boolean;
  canConnect: boolean;
  code?: string;
  message?: string;
  clientType: RemoteAccessClientType;
  /** When the device's agent last reported its remote access state. */
  lastReportedUtc?: string;
  streamer?: StreamerStatus;
}

export interface RemoteAccessSessionLaunch {
  agentId: string;
  sessionId: string;
  clientType: RemoteAccessClientType;
  /** st-rmm:// URI with the session password — never log or persist. */
  launchUri?: string;
  webLaunchUrl?: string;
  webLaunchUrlTtlSeconds?: number;
  expiresInSeconds: number;
  warning?: string;
}

interface ResponseModel<T> {
  isSuccess: boolean;
  message?: string;
  errorCode?: string;
  data?: T;
}

/** Tenant-level gate. Always 200; data.enabled=false means use the legacy Marketplace launch instead. */
export async function getSplashtopFeature(tenantId: string): Promise<SplashtopFeatureStatus | null> {
  const response = await api.get<ResponseModel<SplashtopFeatureStatus>>(`agentapi/rmm/splashtop/${tenantId}/feature`);
  return response.data?.data ?? null;
}

export async function getRemoteAccessAgentStatus(tenantId: string, agentId: string): Promise<RemoteAccessAgentStatus | null> {
  const response = await api.get<ResponseModel<RemoteAccessAgentStatus>>(`agentapi/rmm/splashtop/${tenantId}/agents/${agentId}/status`);
  return response.data?.data ?? null;
}

/**
 * Starts a session and returns the launch data (one round trip, ~1 s). Rejections (409/404/503) throw with
 * response.data { errorCode, message }.
 */
export async function startRemoteAccessSession(tenantId: string, agentId: string): Promise<RemoteAccessSessionLaunch> {
  const response = await api.post<ResponseModel<RemoteAccessSessionLaunch>>(`agentapi/rmm/splashtop/${tenantId}/sessions`, {
    agentId,
  });
  const launch = response.data?.data;
  if (!response.data?.isSuccess || !launch) throw new Error(response.data?.message || 'Remote access could not be started.');
  return launch;
}
